import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/events?q=${encodeURIComponent(q)}` : "/events");
    setKeyword("");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50/70 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-lg">
              <i className="ri-fire-line" />
            </span>
            <span className="leading-none">
              <span className="block font-heading text-lg font-extrabold text-foreground-950 tracking-wide">
                {siteInfo.name}
              </span>
              <span className="mt-0.5 block text-[10px] tracking-[0.2em] text-foreground-500">
                吃瓜时间线
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {navLinks.map((link) => {
              const active =
                link.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`cursor-pointer rounded-md px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                    active
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden lg:flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400"
              role="search"
            >
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索事件 / 标签"
                className="w-36 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </form>
            <Link
              to="/events"
              className="hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-compass-3-line" />
              </span>
              逛事件库
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-3">
          <form
            onSubmit={handleSearch}
            className="mb-3 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2"
            role="search"
          >
            <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索事件 / 标签"
              className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
          </form>
          <nav className="flex flex-col" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}